import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Pie } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend
} from 'chart.js';
import { Helmet } from 'react-helmet';

ChartJS.register(
  ArcElement,
  Tooltip,
  Legend
);

const BudgetCalculator = () => {
  const [inputData, setInputData] = useState({
    income: '',
    housing: '',
    utilities: '',
    food: '',
    transportation: '',
    entertainment: '',
    otherExpenses: ''
  });

  const [results, setResults] = useState(null);
  const [error, setError] = useState('');

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setInputData(prev => ({
      ...prev,
      [name]: value
    }));
    setError('');
  };

  const calculateBudget = () => {
    const { income, housing, utilities, food, transportation, entertainment, otherExpenses } = inputData;

    // Validate inputs
    if (!income) {
      setError('Please enter your income');
      return;
    }

    const cleanIncome = parseFloat(income.replace(/\s/g, ''));
    const cleanHousing = parseFloat(housing.replace(/\s/g, '') || 0);
    const cleanUtilities = parseFloat(utilities.replace(/\s/g, '') || 0);
    const cleanFood = parseFloat(food.replace(/\s/g, '') || 0);
    const cleanTransportation = parseFloat(transportation.replace(/\s/g, '') || 0);
    const cleanEntertainment = parseFloat(entertainment.replace(/\s/g, '') || 0);
    const cleanOtherExpenses = parseFloat(otherExpenses.replace(/\s/g, '') || 0);

    if (isNaN(cleanIncome) || cleanIncome <= 0) {
      setError('Please enter a valid positive number for income');
      return;
    }

    const totalExpenses = cleanHousing + cleanUtilities + cleanFood + cleanTransportation + cleanEntertainment + cleanOtherExpenses;

    if (totalExpenses > cleanIncome) {
      setError('Total expenses cannot exceed income');
      return;
    }

    const remainingBudget = cleanIncome - totalExpenses;

    // Create visualization data
    const expenseLabels = ['Housing', 'Utilities', 'Food', 'Transportation', 'Entertainment', 'Other'];
    const expenseValues = [cleanHousing, cleanUtilities, cleanFood, cleanTransportation, cleanEntertainment, cleanOtherExpenses];
    const expenseColors = [
      'rgba(255, 99, 132, 0.7)',
      'rgba(54, 162, 235, 0.7)',
      'rgba(255, 206, 86, 0.7)',
      'rgba(75, 192, 192, 0.7)',
      'rgba(153, 102, 255, 0.7)',
      'rgba(255, 159, 64, 0.7)'
    ];

    const chartData = {
      labels: expenseLabels,
      datasets: [{
        data: expenseValues,
        backgroundColor: expenseColors,
        borderColor: expenseColors.map(color => color.replace('0.7', '1')),
        borderWidth: 1
      }]
    };

    // Create summary data
    const summaryData = {
      income: cleanIncome,
      totalExpenses,
      remainingBudget,
      expenseBreakdown: [
        { category: 'Housing', amount: cleanHousing },
        { category: 'Utilities', amount: cleanUtilities },
        { category: 'Food', amount: cleanFood },
        { category: 'Transportation', amount: cleanTransportation },
        { category: 'Entertainment', amount: cleanEntertainment },
        { category: 'Other', amount: cleanOtherExpenses }
      ]
    };

    setResults({
      summaryData,
      chartData
    });
  };

  const exampleData = {
    income: '5000',
    housing: '1500',
    utilities: '200',
    food: '600',
    transportation: '300',
    entertainment: '400',
    otherExpenses: '500'
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 1.5 }}
      className="min-h-screen bg-gradient-to-r from-green-100 to-blue-100"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <Helmet>
          <title>Budget Calculator | Financial Planning Tool</title>
          <meta name="description" content="Calculate your monthly budget easily with this tool. Track income and expenses to manage your finances effectively." />
          <meta name="keywords" content="budget calculator, financial planning, expense tracker, income calculator" />
        </Helmet>

        <div className="relative overflow-hidden bg-white rounded-lg shadow-2xl p-8 mb-8">
          <div className="calculator-header flex items-center mb-8">
            <div className="header-icon text-4xl text-green-600 mr-4">💰</div>
            <h1 className="text-3xl font-bold text-gray-800">Budget Calculator</h1>
          </div>

          <div className="text-gray-600 mb-8">
            <p>Track your income and expenses to manage your budget effectively. This tool helps you visualize your spending and plan your finances.</p>
          </div>

          <div className="grid grid-cols-1 gap-6 mb-6">
            <div className="input-group">
              <label className="block text-sm font-medium text-gray-700 mb-2">Monthly Income:</label>
              <input
                type="text"
                name="income"
                value={inputData.income}
                onChange={handleInputChange}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                placeholder="Enter your monthly income (e.g., 5000)"
              />
            </div>

            <div className="input-group">
              <label className="block text-sm font-medium text-gray-700 mb-2">Housing:</label>
              <input
                type="text"
                name="housing"
                value={inputData.housing}
                onChange={handleInputChange}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                placeholder="Enter housing expenses (e.g., 1500)"
              />
            </div>

            <div className="input-group">
              <label className="block text-sm font-medium text-gray-700 mb-2">Utilities:</label>
              <input
                type="text"
                name="utilities"
                value={inputData.utilities}
                onChange={handleInputChange}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                placeholder="Enter utilities expenses (e.g., 200)"
              />
            </div>

            <div className="input-group">
              <label className="block text-sm font-medium text-gray-700 mb-2">Food:</label>
              <input
                type="text"
                name="food"
                value={inputData.food}
                onChange={handleInputChange}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                placeholder="Enter food expenses (e.g., 600)"
              />
            </div>

            <div className="input-group">
              <label className="block text-sm font-medium text-gray-700 mb-2">Transportation:</label>
              <input
                type="text"
                name="transportation"
                value={inputData.transportation}
                onChange={handleInputChange}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                placeholder="Enter transportation expenses (e.g., 300)"
              />
            </div>

            <div className="input-group">
              <label className="block text-sm font-medium text-gray-700 mb-2">Entertainment:</label>
              <input
                type="text"
                name="entertainment"
                value={inputData.entertainment}
                onChange={handleInputChange}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                placeholder="Enter entertainment expenses (e.g., 400)"
              />
            </div>

            <div className="input-group">
              <label className="block text-sm font-medium text-gray-700 mb-2">Other Expenses:</label>
              <input
                type="text"
                name="otherExpenses"
                value={inputData.otherExpenses}
                onChange={handleInputChange}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                placeholder="Enter other expenses (e.g., 500)"
              />
            </div>
          </div>

          <button
            onClick={calculateBudget}
            className="w-full py-3 px-4 bg-green-600 text-white font-medium rounded-lg hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 transition-colors duration-200"
          >
            Calculate Budget
          </button>

          {error && (
            <div className="mt-4 p-4 text-red-700 bg-red-100 rounded-lg">
              {error}
            </div>
          )}

          {results && (
            <div className="mt-8">
              <h2 className="text-2xl font-bold text-gray-800 mb-6">Budget Summary</h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                <div className="bg-gray-50 p-6 rounded-lg">
                  <h3 className="text-lg font-semibold text-gray-700 mb-4">Financial Overview</h3>
                  <div className="space-y-3">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Monthly Income</span>
                      <span>${results.summaryData.income.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Total Expenses</span>
                      <span>${results.summaryData.totalExpenses.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Remaining Budget</span>
                      <span className="font-bold text-green-600">${results.summaryData.remainingBudget.toFixed(2)}</span>
                    </div>
                  </div>
                </div>

                <div className="bg-gray-50 p-6 rounded-lg">
                  <h3 className="text-lg font-semibold text-gray-700 mb-4">Expense Breakdown</h3>
                  <div className="space-y-3">
                    {results.summaryData.expenseBreakdown.map((expense, index) => (
                      <div key={index} className="flex justify-between">
                        <span className="text-gray-600">{expense.category}</span>
                        <span>${expense.amount.toFixed(2)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {results.chartData && (
                <div className="bg-white p-6 rounded-lg shadow-md mb-8">
                  <h3 className="text-lg font-semibold text-gray-700 mb-4">Expense Distribution</h3>
                  <Pie
                    data={results.chartData}
                    options={{
                      responsive: true,
                      maintainAspectRatio: false,
                      plugins: {
                        title: {
                          display: true,
                          text: 'Expense Distribution by Category'
                        },
                        legend: {
                          position: 'bottom'
                        }
                      }
                    }}
                  />
                </div>
              )}

              <div className="bg-gray-50 p-6 rounded-lg">
                <h3 className="text-lg font-semibold text-gray-700 mb-4">Budget Analysis</h3>
                <div className="space-y-4">
                  <p className="text-gray-600">
                    Your remaining budget is <strong>${results.summaryData.remainingBudget.toFixed(2)}</strong>, which can be allocated to savings, investments, or other financial goals.
                  </p>
                  <p className="text-gray-600">
                    To improve your financial health, consider reducing expenses in high-spending categories or increasing your income.
                  </p>
                  <p className="text-gray-600">
                    Use this tool regularly to track your spending patterns and adjust your budget as needed.
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Example Calculation Section */}
        <div className="calculator-section mt-12">
          <h2 className="text-2xl font-bold text-gray-800 mb-6">Example Calculation <span className="text-green-600">📝</span></h2>
          <div className="text-gray-600">
            <p>Here's an example calculation for budget planning:</p>
            
            <div className="bg-white p-6 rounded-lg shadow-md mt-4">
              <h4 className="mb-4">Sample Data:</h4>
              <ul className="list-disc pl-6 mb-4">
                <li>Monthly Income: ${exampleData.income}</li>
                <li>Housing: ${exampleData.housing}</li>
                <li>Utilities: ${exampleData.utilities}</li>
                <li>Food: ${exampleData.food}</li>
                <li>Transportation: ${exampleData.transportation}</li>
                <li>Entertainment: ${exampleData.entertainment}</li>
                <li>Other Expenses: ${exampleData.otherExpenses}</li>
              </ul>

              <h4 className="mb-4">Results:</h4>
              <ul className="list-disc pl-6 mb-4">
                <li>Total Expenses: $3500</li>
                <li>Remaining Budget: $1500</li>
              </ul>
            </div>
          </div>
        </div>

        <div className="calculator-section mt-12">
          <h2 className="text-2xl font-bold text-gray-800 mb-6">How to Use <span className="text-green-600">📋</span></h2>
          <div className="text-gray-600">
            <p>Follow these steps to use the Budget Calculator:</p>
            <ol className="list-decimal pl-6 mt-4">
              <li><strong>Enter Monthly Income:</strong>
                <ul className="list-disc pl-6 mt-2">
                  <li>Input your total monthly income</li>
                </ul>
              </li>
              <li><strong>Enter Expenses:</strong>
                <ul className="list-disc pl-6 mt-2">
                  <li>Input your expenses for each category</li>
                  <li>Leave a field empty if there's no expense for that category</li>
                </ul>
              </li>
              <li><strong>Click Calculate:</strong> The calculator will:
                <ul className="list-disc pl-6 mt-2">
                  <li>Compute your total expenses</li>
                  <li>Show your remaining budget</li>
                  <li>Display a visual breakdown of your expenses</li>
                </ul>
              </li>
            </ol>
            <div className="mt-4 p-4 bg-green-100 rounded-lg">
              <p><strong>💡 Pro Tips:</strong></p>
              <ul className="list-disc pl-6 mt-2">
                <li>Track your expenses regularly to stay on budget</li>
                <li>Set financial goals to improve your budgeting</li>
                <li>Adjust your spending habits based on the results</li>
              </ul>
            </div>
          </div>
        </div>

        <div className="calculator-section mt-12">
          <h2 className="text-2xl font-bold text-gray-800 mb-6">Budgeting Tips <span className="text-green-600">✨</span></h2>
          <div className="text-gray-600">
            <ul className="list-disc pl-6 mt-4">
              <li><strong>50/30/20 Rule:</strong> Allocate 50% of income to needs, 30% to wants, and 20% to savings</li>
              <li><strong>Emergency Fund:</strong> Save 3-6 months' worth of expenses for emergencies</li>
              <li><strong>Debt Reduction:</strong> Prioritize paying off high-interest debt</li>
              <li><strong>Track Spending:</strong> Regularly review your expenses to identify areas for improvement</li>
            </ul>
            <p className="italic mt-4">
              Note: Everyone's financial situation is unique. Adjust these guidelines to fit your personal circumstances.
            </p>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default BudgetCalculator;